import { useEffect, useRef } from 'react'
import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js'
import {
  BOGGLE_INTERVAL_PREP_COUNT,
  BOGGLE_INTERVAL_PREP_STEP_MS,
  BOGGLE_RESOLVE_ANIMATION_MS,
  BOGGLE_SPAWN_START_ROWS_ABOVE,
} from '~/boggle/config/defaults'
import { getBoggleResolvedRequiredLetter } from '~/boggle/lib/challenge/resolvedModOps/outcomeQueries'
import {
  BOGGLE_GOLD_SCORE_BONUS,
  BOGGLE_GOLD_TILE_KIND,
  getSpecialTilePreviewFillNumber,
  getSpecialTilePreviewTextColorNumber,
  resolveLiveCellDisplayKind,
  type BoggleSpecialTileKind,
} from '~/boggle/engine/specialTiles'
import {
  pushBoggleAmbientBoardEffect,
  resetBoggleBoardSurfaceBridge,
  setBoggleBoardSurfaceSnapshot,
} from '~/boggle/ui/ambient/boardSurfaceBridge'
import {
  drawTileShapePath,
  getTileShapeKind,
  isPointInsideTileShape,
  type TileShapeKind,
} from './tileShapeDraw'
import type { BogglePosition } from '~/boggle/engine/standard/types'
import {
  isIntervalPrepBlockingPlay,
  isPlayableSelectionPosition,
} from '~/boggle/engine/standard/game'
import { isBoggleGoldRushActive } from '~/boggle/engine/standard/effectiveSpecialTiles'
import { RARE_LETTERS } from '~/boggle/config/boardDistributions/letterWeights'
import { BOGGLE_UNCOMMON_LETTERS } from '~/boggle/config/challengeModLetterConstants'
import { BOGGLE_VOWELS } from '~/boggle/engine/standard/refill'
import type { BoggleStore } from '~/boggle/ui/state/Store'
import pixiCanvas from '../PixiCanvas.module.css'

export type BoardLayout = {
  cellGap: number
  cellSize: number
  originX: number
  originY: number
  tileSize: number
}

type TileVisual = {
  bonusBadge: Text
  goldBadge: Graphics
  goldOutline: Graphics
  letter: Text
  letterCircle: Graphics
  letterDiamond: Graphics
  letterSquare: Graphics
  selectedInnerOutline: Graphics
  shadow: Graphics
  tile: Graphics
  tileOutline: Graphics
}

type CanvasMetrics = {
  cols: number
  layout: BoardLayout
  rect: DOMRect
  rendererHeight: number
  rendererWidth: number
  rows: number
}

export type { TileShapeKind } from './tileShapeDraw'
export type TileHitTargetMode = 'idle' | 'drag'

const BOARD_PADDING = 4
const TILE_FILL = 0x000000
const TILE_ACTIVE = 0xe2b96a
const TILE_PRESSED = 0xc1994f
const TILE_ACTIVE_TEXT = 0x1d1510
const TILE_TEXT = 0xf7eedf
/** Slightly desaturated yellow for prep countdown numerals */
const PREP_COUNTDOWN_TEXT = 0xd8c896
const SWAP_FILL = 0xb68cff
const SWAP_TEXT = 0x241636
const COPY_FILL = 0x7bc8ff
const COPY_TEXT = 0x0f2740
const GOLD_BADGE_FILL = 0xf4cd57
const GOLD_BADGE_TEXT = 0x3a2905
const PLAYABLE_OUTLINE = 0xffffff
const PLAYABLE_OUTLINE_ALPHA = 0.3
const PLAYED_OUTLINE_ALPHA = 0.4
/** Border for every tile whose letter matches Anchor Letter (required-letter) */
const ANCHOR_LETTER_OUTLINE = 0xffffff
const ANCHOR_LETTER_OUTLINE_ALPHA = 0.88
const PLAYABLE_OUTLINE_WIDTH = 3
const PLAYED_OUTLINE_OFFSET = 1
const PATH_ALPHA = 0.6
const CHEVRON_ALPHA = 1
const PATH_WIDTH = 4
/** Tessellate curved strokes into short segments for smooth gradient along stroke (~px per step). */
const PATH_RAINBOW_STEP_PX = 7
const PATH_RAINBOW_STEP_MIN = 10
/** Letters along selection over which stroke hue goes yellow → red; further tiles stay fully red. */
const PATH_SELECTION_YELLOW_TO_RED_LETTER_SPAN = 7
/** Yellow hue (degrees); ramps down toward 0 (red). */
const PATH_SELECTION_YELLOW_HUE_DEG = 50
/** Saturation used once past the desaturated warm-up (matches yellow→red ramp). */
const PATH_SELECTION_PATH_SATURATION = 0.7
/** First N letter indices ramp saturation from muted yellow up to {@link PATH_SELECTION_PATH_SATURATION}. */
const PATH_SELECTION_DESAT_YELLOW_LETTER_COUNT = 1
const PATH_SELECTION_DESAT_SATURATION = 0.5
/**
 * Final desat-phase letter stops short of full saturation (ratio toward {@link PATH_SELECTION_PATH_SATURATION});
 * remaining saturation blends on the next connector into the yellow→red ramp.
 */
const PATH_SELECTION_DESAT_LAST_LETTER_SATURATION_BLEND = 0.62
/** Ease-in along first connector (slow climb → lingers muted longer on the stroke). */
const PATH_SELECTION_DESAT_STROKE_BLEND_POWER = 4
/** Same-tile doubled letter: arc sits toward tile top-right (center offset vs tile size). */
const PATH_SAME_TILE_LOOP_CENTER_OFFSET_RATIO = 0.3
const PATH_SAME_TILE_LOOP_RADIUS_RATIO = 0.18
/** Mostly-closed ring (~265°); gap reads like a refresh icon wedge toward tile interior. */
const PATH_SAME_TILE_LOOP_START_ANGLE_RADIANS = Math.PI * 0.5
const PATH_SAME_TILE_LOOP_SWEEP_RADIANS = -Math.PI * 1.42
/**
 * Selection strokes bow perpendicular to travel direction via `(nx, ny) = (uy, -ux)`:
 * down → right, up → left, right → up, left → down — keeps paths off tile glyphs.
 */
const PATH_BOW_ENDPOINT_OFFSET_PX = 8
const PATH_BOW_CONTROL_DEPTH_PX = 10
const PATH_BOW_SCALE_STUB_DIVISOR_RATIO = 1.35
/** Matches the vowel letter halo circle diameter vs tile size (`letterCircle`). */
const VOWEL_LETTER_CIRCLE_DIAMETER_RATIO = 0.65
/** Chevron sits this far back from the destination tile center along the inbound chord (ratio of tileSize). */
const PATH_CHEVRON_ANCHOR_INSET_FROM_TILE_CENTER_RATIO = 0.3
/** Chevron apex offset past anchor toward tile center — ratio of tile size. */
const PATH_CHEVRON_INSIDE_CROSSING_RATIO = 0.1
const PATH_CHEVRON_DEPTH_RATIO = 0.2
const PATH_CHEVRON_HALF_WIDTH_RATIO = 0.1
/** Min gap between symmetric path trims along a chord (`chord - 2 * endpointInset`). */
const PATH_SEGMENT_MIDSTUB_MIN_PX = 10
/** Last tile in selection: filled dot (~15px diameter, CSS px) */
const SELECTION_END_MARKER_RADIUS = 10
const SELECTION_END_MARKER_ALPHA = 0.6
const TILE_SHADOW_COLOR = 0x0a0908
const TILE_SHADOW_ALPHA = 0.14
const TILE_IDLE_ALPHA = 0.92
const TILE_EMPHASIS_ALPHA = 0.96
const TILE_HIT_RADIUS_RATIO = 0.82
const IDLE_TILE_CONTACT_GAP_PX = 4
const TAP_DRAG_THRESHOLD_PX = 10
const BOARD_SCALE = 0.98
const PLAYED_SEPARATOR_COLOR = 0x000000
const PLAYED_SEPARATOR_ALPHA = 1
const PLAYED_SEPARATOR_WIDTH = 2
const PLAYED_INACTIVE_LETTER_ALPHA = 0.7
const LETTER_FULL_ALPHA = 1
/** Uncommon square fill/stroke (independent of vowel circle) */
const UNCOMMON_LETTER_ACCENT = 0xbb_55_55
const VOWEL_AND_UNCOMMON_LETTER_ACCENT = 0xbb_00_00
const UNPLAYABLE_LETTER_ALPHA = 0.8
const UNPLAYABLE_TILE_FILL_ALPHA_OFFSET = 0.1
const TILE_PRESSED_SCALE = 0.9
const EMPTY_SLOT_FILL = 0xc2c0bb
const EMPTY_SLOT_ALPHA = 0.42
const EMPTY_SLOT_RADIUS_RATIO = 0.1
const PIXI_MAX_RENDER_RESOLUTION = 2
/** Brief hold at full yellow before the fade segment */
const SELECTION_FILL_FLASH_HOLD_MS = 56
/** Ease-out fade from yellow back to idle (CSS-like ease-out) */
const SELECTION_FILL_FADE_OUT_MS = 500
function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function getPixiRenderResolution() {
  if (typeof window === 'undefined') {
    return 1
  }

  return clamp(window.devicePixelRatio || 1, 1, PIXI_MAX_RENDER_RESOLUTION)
}

function snapToPixel(value: number, resolution: number) {
  return Math.round(value * resolution) / resolution
}

/**
 * Ensures webfonts used by Pixi {@link Text} are available before measure/draw (avoids stuck sans-serif fallback).
 * @see https://developer.mozilla.org/en-US/docs/Web/API/FontFaceSet
 */
async function waitForBogglePixiFonts() {
  if (typeof document === 'undefined' || !document.fonts?.ready) {
    return
  }
  await document.fonts.ready
  const specs = ['600 48px "Noto Sans Variable"', '800 11px "Noto Sans Variable"']
  await Promise.all(specs.map(spec => document.fonts.load(spec))).catch(() => undefined)
}

export function getRoundedLetterFontSize(tileSize: number, resolution: number) {
  return Math.max(1, snapToPixel((tileSize * 2) / 5, resolution))
}

function easeOutCubic(progress: number) {
  return 1 - (1 - progress) ** 3
}

function easeInPow(progress: number, power: number) {
  return clamp(progress, 0, 1) ** power
}

function mixRgb(from: number, to: number, t: number) {
  const tt = clamp(t, 0, 1)
  const rb = (from >> 16) & 255
  const gb = (from >> 8) & 255
  const bb = from & 255
  const ra = (to >> 16) & 255
  const ga = (to >> 8) & 255
  const ba = to & 255
  const r = Math.round(rb + (ra - rb) * tt)
  const g = Math.round(gb + (ga - gb) * tt)
  const b = Math.round(bb + (ba - bb) * tt)
  return (r << 16) | (g << 8) | b
}

function hslToPixiColor(h: number, s: number, l: number): number {
  const hh = ((((h % 360) + 360) % 360) / 360)
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hue2rgb = (t: number) => {
    let tt = t
    if (tt < 0) {
      tt += 1
    }
    if (tt > 1) {
      tt -= 1
    }
    if (tt < 1 / 6) {
      return p + (q - p) * 6 * tt
    }
    if (tt < 1 / 2) {
      return q
    }
    if (tt < 2 / 3) {
      return p + (q - p) * (2 / 3 - tt) * 6
    }
    return p
  }

  const r = Math.round(hue2rgb(hh + 1 / 3) * 255)
  const g = Math.round(hue2rgb(hh) * 255)
  const b = Math.round(hue2rgb(hh - 1 / 3) * 255)
  return (r << 16) | (g << 8) | b
}

/** Stroke color by letter index: desaturated yellow → full yellow → red, then clamps at red. */
function selectionPathLetterColor(letterIndex: number): number {
  const desatSteps = PATH_SELECTION_DESAT_YELLOW_LETTER_COUNT
  if (letterIndex < desatSteps) {
    const denom = Math.max(desatSteps - 1, 1)
    let u = clamp(letterIndex / denom, 0, 1)
    if (letterIndex === desatSteps - 1 && desatSteps >= 2) {
      u *= PATH_SELECTION_DESAT_LAST_LETTER_SATURATION_BLEND
    }
    const s =
      PATH_SELECTION_DESAT_SATURATION +
      (PATH_SELECTION_PATH_SATURATION - PATH_SELECTION_DESAT_SATURATION) * u
    return hslToPixiColor(PATH_SELECTION_YELLOW_HUE_DEG, s, 0.54)
  }

  const huePhaseIndex = letterIndex - desatSteps
  const span = Math.max(PATH_SELECTION_YELLOW_TO_RED_LETTER_SPAN, 1)
  const t = clamp(huePhaseIndex / span, 0, 1)
  const hue = PATH_SELECTION_YELLOW_HUE_DEG * (1 - t)
  return hslToPixiColor(hue, PATH_SELECTION_PATH_SATURATION, 0.54)
}

function quadraticBezierXY(
  sx: number,
  sy: number,
  cx: number,
  cy: number,
  ex: number,
  ey: number,
  t: number,
): { x: number; y: number } {
  const u = 1 - t
  const uu = u * u
  const tt = t * t
  return {
    x: uu * sx + 2 * u * t * cx + tt * ex,
    y: uu * sy + 2 * u * t * cy + tt * ey,
  }
}

function selectionFillHighlightStrength(elapsedMs: number): number {
  if (elapsedMs <= SELECTION_FILL_FLASH_HOLD_MS) {
    return 1
  }

  const fadeT =
    (elapsedMs - SELECTION_FILL_FLASH_HOLD_MS) / SELECTION_FILL_FADE_OUT_MS
  const fadeProgress = clamp(fadeT, 0, 1)
  return 1 - easeOutCubic(fadeProgress)
}

function easeInCubic(progress: number) {
  return progress ** 3
}

function easeInOutSine(progress: number) {
  return -(Math.cos(Math.PI * progress) - 1) / 2
}

function createLayout(
  width: number,
  height: number,
  rows: number,
  cols: number,
): BoardLayout {
  const availableWidth = Math.max(1, (width - BOARD_PADDING * 2) * BOARD_SCALE)
  const availableHeight = Math.max(1, (height - BOARD_PADDING * 2) * BOARD_SCALE)
  const cellSize = Math.max(1, Math.min(availableWidth / cols, availableHeight / rows))
  const cellGap = Math.max(0, Math.floor(cellSize * 0.18))
  const tileSize = Math.max(1, cellSize - cellGap)
  const boardWidth = cellSize * cols
  const boardHeight = cellSize * rows
  return {
    cellGap,
    cellSize,
    originX: Math.round((width - boardWidth) / 2),
    originY: Math.round((height - boardHeight) / 2),
    tileSize,
  }
}

export { getRegularPolygonPoints, getTileShapeKind, isPointInsideTileShape } from './tileShapeDraw'

function getIdleTileFillColor(displayKind: BoggleSpecialTileKind | null) {
  if (displayKind) {
    return getSpecialTilePreviewFillNumber(displayKind)
  }

  return TILE_FILL
}

function getIdleTileTextColor(displayKind: BoggleSpecialTileKind | null) {
  if (displayKind) {
    return getSpecialTilePreviewTextColorNumber(displayKind)
  }

  return TILE_TEXT
}

export function isPointInsideIdleTileBounds(
  x: number,
  y: number,
  cellSize: number,
  gapPx = IDLE_TILE_CONTACT_GAP_PX,
) {
  const inset = gapPx / 2
  const max = cellSize - inset

  return x >= inset && x <= max && y >= inset && y <= max
}

export function getPointerCell(
  pointerX: number,
  pointerY: number,
  layout: BoardLayout,
  rows: number,
  cols: number,
  getTileShapeAtPosition: (position: BogglePosition) => TileShapeKind,
  targetMode: TileHitTargetMode,
): BogglePosition | null {
  const localX = pointerX - layout.originX
  const localY = pointerY - layout.originY
  if (localX < 0 || localY < 0) {
    return null
  }

  const col = Math.floor(localX / layout.cellSize)
  const row = Math.floor(localY / layout.cellSize)
  if (col < 0 || col >= cols || row < 0 || row >= rows) {
    return null
  }

  const cellLocalX = localX - col * layout.cellSize
  const cellLocalY = localY - row * layout.cellSize
  if (targetMode === 'idle') {
    if (!isPointInsideIdleTileBounds(cellLocalX, cellLocalY, layout.cellSize)) {
      return null
    }

    return { col, row }
  }

  const centerX = col * layout.cellSize + layout.cellSize / 2
  const centerY = row * layout.cellSize + layout.cellSize / 2
  const deltaX = localX - centerX
  const deltaY = localY - centerY
  const hitRadius = (layout.tileSize / 2) * TILE_HIT_RADIUS_RATIO
  const shape = getTileShapeAtPosition({ col, row })

  if (!isPointInsideTileShape(deltaX, deltaY, hitRadius, shape)) {
    return null
  }

  return { col, row }
}

function positionsMatch(a: BogglePosition | null, b: BogglePosition) {
  return a !== null && a.row === b.row && a.col === b.col
}

function nullablePositionsMatch(a: BogglePosition | null, b: BogglePosition | null) {
  return (a === null && b === null) || (a !== null && b !== null && positionsMatch(a, b))
}

export function getNextDragTrigger(
  lastTriggeredPosition: BogglePosition | null,
  position: BogglePosition | null,
) {
  if (position === null) {
    return {
      nextTriggeredPosition: null,
      shouldTrigger: false,
    }
  }

  if (positionsMatch(lastTriggeredPosition, position)) {
    return {
      nextTriggeredPosition: lastTriggeredPosition,
      shouldTrigger: false,
    }
  }

  return {
    nextTriggeredPosition: position,
    shouldTrigger: true,
  }
}

function positionCenter(
  layout: BoardLayout,
  position: BogglePosition,
  visualRow = position.row,
) {
  return {
    x: layout.originX + position.col * layout.cellSize + layout.cellSize / 2,
    y: layout.originY + visualRow * layout.cellSize + layout.cellSize / 2,
  }
}

function positionKey(position: BogglePosition) {
  return `${position.row}:${position.col}`
}

function vowelLetterCircleRadiusPx(layout: BoardLayout, tilePressedScale: number): number {
  return ((layout.tileSize * VOWEL_LETTER_CIRCLE_DIAMETER_RATIO) / 2) * tilePressedScale
}

function drawSelectionPathChevronTowardInbound(
  graphics: Graphics,
  approachAnchorOnChordX: number,
  approachAnchorOnChordY: number,
  ux: number,
  uy: number,
  tileSize: number,
  fillColor: number,
) {
  const { past, depth, halfSpread } = selectionChevronShapePx(tileSize)

  const tipX = approachAnchorOnChordX + ux * past
  const tipY = approachAnchorOnChordY + uy * past

  const backX = tipX - ux * depth
  const backY = tipY - uy * depth

  const px = -uy
  const py = ux

  graphics
    .moveTo(tipX, tipY)
    .lineTo(backX + px * halfSpread, backY + py * halfSpread)
    .lineTo(backX - px * halfSpread, backY - py * halfSpread)
    .closePath()
    .fill({ alpha: CHEVRON_ALPHA, color: fillColor })
}

/** Circular “refresh” stroke when two consecutive picks share one tile (top-right of glyph area). */
function drawDoubledTileRepeatArcStroke(
  graphics: Graphics,
  tileCenterX: number,
  tileCenterY: number,
  tileSize: number,
  colorFrom: number,
  colorTo: number,
) {
  const offset = tileSize * PATH_SAME_TILE_LOOP_CENTER_OFFSET_RATIO
  const arcCx = tileCenterX + offset + 5
  const arcCy = tileCenterY - offset + 5
  const R = tileSize * PATH_SAME_TILE_LOOP_RADIUS_RATIO
  const sweep = PATH_SAME_TILE_LOOP_SWEEP_RADIANS
  const angleStart = PATH_SAME_TILE_LOOP_START_ANGLE_RADIANS
  const arcLenApprox = Math.abs(sweep) * R
  const steps = Math.max(16, Math.ceil(arcLenApprox / PATH_RAINBOW_STEP_PX))

  let ax = arcCx + Math.cos(angleStart) * R
  let ay = arcCy + Math.sin(angleStart) * R
  let tanUx = Math.cos(angleStart + sweep)
  let tanUy = Math.sin(angleStart + sweep)

  for (let step = 1; step <= steps; step += 1) {
    const u1 = step / steps
    const angle = angleStart + sweep * u1
    const bx = arcCx + Math.cos(angle) * R
    const by = arcCy + Math.sin(angle) * R
    const tm = ((step - 1) / steps + u1) / 2
    const dx = bx - ax
    const dy = by - ay
    const segLen = Math.hypot(dx, dy)
    if (segLen > 1e-6) {
      tanUx = dx / segLen
      tanUy = dy / segLen
    }

    graphics
      .moveTo(ax, ay)
      .lineTo(bx, by)
      .stroke({
        alpha: PATH_ALPHA,
        color: mixRgb(colorFrom, colorTo, tm),
        width: PATH_WIDTH,
      })

    ax = bx
    ay = by
  }

  drawSelectionPathChevronTowardInbound(
    graphics,
    ax,
    ay,
    tanUx,
    tanUy,
    tileSize,
    mixRgb(colorTo, 0x000000, 0.22),
  )
}

function selectionChevronShapePx(tileSize: number): {
  past: number
  depth: number
  halfSpread: number
  /** Half the chevron axial span along the path direction (`past + depth`). */
  halfChordLengthPx: number
} {
  const past = Math.max(2, tileSize * PATH_CHEVRON_INSIDE_CROSSING_RATIO)
  const depth = Math.max(4.5, tileSize * PATH_CHEVRON_DEPTH_RATIO)
  const halfSpread = Math.max(2.8, tileSize * PATH_CHEVRON_HALF_WIDTH_RATIO)
  const halfChordLengthPx = (past + depth) / 2

  return { past, depth, halfChordLengthPx, halfSpread }
}

export function useBogglePixiRuntime({
  hostRef,
  store,
}: {
  hostRef: React.RefObject<HTMLDivElement | null>
  store: BoggleStore
}) {
  const appRef = useRef<Application | null>(null)
  const canvasMetricsRef = useRef<CanvasMetrics | null>(null)
  const pointerDownRef = useRef(false)
  const activePointerIdRef = useRef<number | null>(null)
  const pointerDragActiveRef = useRef(false)
  const pointerStartCellRef = useRef<BogglePosition | null>(null)
  const pointerStartCoordsRef = useRef<{ x: number; y: number } | null>(null)
  const pointerLastTriggeredCellRef = useRef<BogglePosition | null>(null)
  const pointerPressedCellRef = useRef<BogglePosition | null>(null)

  useEffect(() => {
    let mounted = true
    let cleanup: (() => void) | undefined

    async function setup() {
      const host = hostRef.current
      if (!mounted || !host) {
        return
      }

      await waitForBogglePixiFonts()

      const app = new Application()
      await app.init({
        antialias: true,
        autoDensity: true,
        backgroundAlpha: 0,
        height: Math.max(1, host.clientHeight),
        resolution: getPixiRenderResolution(),
        roundPixels: true,
        width: Math.max(1, host.clientWidth),
      })

      if (!mounted || !hostRef.current) {
        await app.destroy()
        return
      }

      const boardGraphics = new Graphics()
      const pathGraphics = new Graphics()
      const tileGraphicsLayer = new Container()
      /** Vowel circle + uncommon letter square — below selection path stroke. */
      const tileLetterAccentLayer = new Container()
      /** Letter glyphs and bonus label — above path. */
      const tileTextLayer = new Container()
      const clearedTextLayer = new Container()
      const scene = new Container()

      let pointerLostTileSinceLastTrigger = false

      scene.addChild(boardGraphics)
      scene.addChild(tileGraphicsLayer)
      scene.addChild(tileLetterAccentLayer)
      scene.addChild(pathGraphics)
      scene.addChild(tileTextLayer)
      scene.addChild(clearedTextLayer)

      const prepCountdownStyle = new TextStyle({
        fill: PREP_COUNTDOWN_TEXT,
        fontSize: 96,
        fontWeight: '700',
        letterSpacing: 4,
      })
      const prepCountdownText = new Text({
        style: prepCountdownStyle,
        text: '',
      })
      prepCountdownText.anchor.set(0.5)
      prepCountdownText.visible = false
      scene.addChild(prepCountdownText)

      app.stage.addChild(scene)

      app.canvas.className = pixiCanvas.canvas
      app.canvas.setAttribute('aria-label', 'Boggle board')
      app.canvas.setAttribute('data-testid', 'boggle-canvas')
      host.appendChild(app.canvas)

      const letterStyle = new TextStyle({
        fill: TILE_TEXT,
        fontFamily: "'Noto Sans Variable', sans-serif",
        fontSize: 34,
        fontWeight: '600',
        letterSpacing: 2,
      })
      const vowelLetterStyle = new TextStyle({
        fill: TILE_TEXT,
        fontFamily: "'Noto Sans Variable', sans-serif",
        fontSize: 34,
        fontWeight: '600',
        letterSpacing: 2,
      })
      const consonantDeclaredFontSize = letterStyle.fontSize
      const vowelDeclaredFontSize = vowelLetterStyle.fontSize
      const goldBadgeStyle = new TextStyle({
        fill: GOLD_BADGE_TEXT,
        fontFamily: "'Noto Sans Variable', sans-serif",
        fontSize: 11,
        fontWeight: '800',
      })
      const refreshPixiTextStyles = () => {
        prepCountdownStyle.update()
        letterStyle.update()
        vowelLetterStyle.update()
        goldBadgeStyle.update()
      }
      if (typeof document !== 'undefined' && document.fonts?.addEventListener) {
        document.fonts.addEventListener('loadingdone', refreshPixiTextStyles)
      }
      let tileVisuals: TileVisual[] = []
      let tileVisualGridKey = ''
      let clearedLetterVisuals: Text[] = []
      let cachedLayout: BoardLayout | null = null
      let cachedLayoutKey = ''
      let selectionPulseWallClockStartsMs = new Map<string, number>()
      let prevSelectionKeysForPulse = new Set<string>()
      let prevSelectionPathForPulse: BogglePosition[] = []
      let selectionFillPulseTickerFn: (() => void) | null = null
      function pushTilePressPulseEffect(
        pulseState: ReturnType<typeof store.getState>,
        position: BogglePosition,
        wallClockMs: number,
      ) {
        const cell = pulseState.board[position.row]?.[position.col]
        if (!cell) {
          return
        }

        pushBoggleAmbientBoardEffect({
          type: 'tilePressPulse',
          position: { col: position.col, row: position.row },
          tileShape: getTileShapeKind(cell, isBoggleGoldRushActive(pulseState)),
          startedAtMs: wallClockMs,
        })
      }

      function syncSelectionPulseWallClockStarts(
        selectionPositions: readonly BogglePosition[],
        wallClockMs: number,
      ) {
        const keys = new Set(selectionPositions.map(positionKey))

        const pathUnchanged =
          selectionPositions.length === prevSelectionPathForPulse.length &&
          selectionPositions.every((position, index) =>
            positionsMatch(position, prevSelectionPathForPulse[index]!),
          )

        if (pathUnchanged) {
          prevSelectionKeysForPulse = keys
          return
        }

        const prevPath = prevSelectionPathForPulse
        const isRemoveOneTrailingTile =
          selectionPositions.length === prevPath.length - 1 &&
          prevPath.length > 0 &&
          selectionPositions.every((position, index) =>
            positionsMatch(position, prevPath[index]!),
          )

        if (isRemoveOneTrailingTile) {
          const removed = prevPath[prevPath.length - 1]!
          pushTilePressPulseEffect(store.getState(), removed, wallClockMs)
          for (const key of prevSelectionKeysForPulse) {
            if (!keys.has(key)) {
              selectionPulseWallClockStartsMs.delete(key)
            }
          }

          prevSelectionKeysForPulse = keys
          prevSelectionPathForPulse = selectionPositions.slice()
          return
        }

        const isAppendOneTile =
          selectionPositions.length === prevPath.length + 1 &&
          selectionPositions
            .slice(0, prevPath.length)
            .every((position, index) => positionsMatch(position, prevPath[index]!))

        if (isAppendOneTile) {
          const appended = selectionPositions[selectionPositions.length - 1]!
          pushTilePressPulseEffect(store.getState(), appended, wallClockMs)
          selectionPulseWallClockStartsMs.set(positionKey(appended), wallClockMs)
        } else {
          for (const position of selectionPositions) {
            selectionPulseWallClockStartsMs.set(positionKey(position), wallClockMs)
          }
        }

        for (const key of prevSelectionKeysForPulse) {
          if (!keys.has(key)) {
            selectionPulseWallClockStartsMs.delete(key)
          }
        }

        prevSelectionKeysForPulse = keys
        prevSelectionPathForPulse = selectionPositions.slice()
      }

      function selectionFillPulseStrength(key: string, wallClockMs: number): number {
        const startedAt = selectionPulseWallClockStartsMs.get(key)
        if (startedAt === undefined) {
          return 0
        }

        return selectionFillHighlightStrength(wallClockMs - startedAt)
      }

      function selectionFillPulseNeedsTicker(
        tileSelection: readonly BogglePosition[],
        wallClockMs: number,
      ): boolean {
        return tileSelection.some(
          pos => selectionFillPulseStrength(positionKey(pos), wallClockMs) > 0.002,
        )
      }

      const hideTileVisual = (visual: TileVisual) => {
        visual.bonusBadge.visible = false
        visual.goldBadge.visible = false
        visual.goldOutline.visible = false
        visual.letter.visible = false
        visual.letterCircle.visible = false
        visual.letterDiamond.visible = false
        visual.letterSquare.visible = false
        visual.selectedInnerOutline.visible = false
        visual.shadow.visible = false
        visual.tile.visible = false
        visual.tileOutline.visible = false
      }

      const createTileVisual = () => {
        const shadow = new Graphics()
        const tileOutline = new Graphics()
        const selectedInnerOutline = new Graphics()
        const goldOutline = new Graphics()
        const tile = new Graphics()
        const goldBadge = new Graphics()
        const letter = new Text({
          style: letterStyle,
          text: '',
        })
        const letterCircle = new Graphics()
        const letterDiamond = new Graphics()
        const letterSquare = new Graphics()
        const bonusBadge = new Text({
          style: goldBadgeStyle,
          text: `+${BOGGLE_GOLD_SCORE_BONUS}`,
        })

        letter.anchor.set(0.5)
        bonusBadge.anchor.set(0.5)

        tileGraphicsLayer.addChild(
          shadow,
          goldOutline,
          tile,
          tileOutline,
          selectedInnerOutline,
          goldBadge,
        )
        tileLetterAccentLayer.addChild(letterSquare, letterDiamond, letterCircle)
        tileTextLayer.addChild(letter, bonusBadge)

        const visual = {
          bonusBadge,
          goldBadge,
          goldOutline,
          letter,
          letterCircle,
          letterDiamond,
          letterSquare,
          selectedInnerOutline,
          shadow,
          tile,
          tileOutline,
        } satisfies TileVisual

        hideTileVisual(visual)

        return visual
      }

      const destroyTileVisuals = () => {
        for (const child of tileGraphicsLayer.removeChildren()) {
          child.destroy()
        }
        for (const child of tileLetterAccentLayer.removeChildren()) {
          child.destroy()
        }
        for (const child of tileTextLayer.removeChildren()) {
          child.destroy()
        }
        tileVisuals = []
        tileVisualGridKey = ''
      }

      const ensureTileVisuals = (rows: number, cols: number) => {
        const gridKey = `${rows}x${cols}`
        const visualCount = rows * cols
        if (tileVisualGridKey === gridKey && tileVisuals.length === visualCount) {
          return
        }

        destroyTileVisuals()
        for (let index = 0; index < visualCount; index += 1) {
          tileVisuals.push(createTileVisual())
        }
        tileVisualGridKey = gridKey
      }

      const ensureClearedLetterVisuals = (count: number) => {
        while (clearedLetterVisuals.length < count) {
          const letter = new Text({
            style: letterStyle,
            text: '',
          })
          letter.anchor.set(0.5)
          letter.visible = false
          clearedTextLayer.addChild(letter)
          clearedLetterVisuals.push(letter)
        }
      }

      const syncCanvasMetrics = (
        state: ReturnType<typeof store.getState>,
        options: {
          layout?: BoardLayout
          measureRect?: boolean
        } = {},
      ) => {
        const layout =
          options.layout ??
          createLayout(app.renderer.width, app.renderer.height, state.rows, state.cols)
        const rect =
          options.measureRect || canvasMetricsRef.current === null
            ? app.canvas.getBoundingClientRect()
            : canvasMetricsRef.current.rect

        canvasMetricsRef.current = {
          cols: state.cols,
          layout,
          rect,
          rendererHeight: app.renderer.height,
          rendererWidth: app.renderer.width,
          rows: state.rows,
        }

        setBoggleBoardSurfaceSnapshot({
          cols: state.cols,
          layout,
          rect,
          rendererHeight: app.renderer.height,
          rendererWidth: app.renderer.width,
          rows: state.rows,
        })
      }

      const draw = () => {
        const state = store.getState()
        const width = app.renderer.width
        const height = app.renderer.height
        const renderResolution = app.renderer.resolution
        const layoutKey = `${width}:${height}:${state.rows}:${state.cols}`
        if (cachedLayout === null || cachedLayoutKey !== layoutKey) {
          cachedLayout = createLayout(width, height, state.rows, state.cols)
          cachedLayoutKey = layoutKey
        }
        const layout = cachedLayout
        const selection = state.selection
        const wallClockMs = performance.now()
        syncSelectionPulseWallClockStarts(selection, wallClockMs)
        const pressedCell = pointerPressedCellRef.current
        const pressedKey = pressedCell === null ? null : positionKey(pressedCell)
        const letterFontSize = getRoundedLetterFontSize(layout.tileSize, renderResolution)
        const letterFontScale = letterFontSize / consonantDeclaredFontSize
        const scaledVowelFontSize = Math.round(vowelDeclaredFontSize * letterFontScale)
        if (letterStyle.fontSize !== letterFontSize) {
          letterStyle.fontSize = letterFontSize
        }
        if (vowelLetterStyle.fontSize !== scaledVowelFontSize) {
          vowelLetterStyle.fontSize = scaledVowelFontSize
        }
        const resolution = state.lastResolution
        const movementWindowMs =
          resolution === null
            ? BOGGLE_RESOLVE_ANIMATION_MS
            : resolution.movementDurationMs ?? resolution.durationMs
        const animationProgress =
          resolution === null
            ? 1
            : clamp(
                (state.timeMs - resolution.startedAtMs) / movementWindowMs,
                0,
                1,
              )
        const easedProgress = easeOutCubic(animationProgress)
        const movedLookup = new Map(
          (resolution?.moved ?? []).map(entry => [entry.id, entry]),
        )
        const spawnedLookup = new Map(
          (resolution?.spawned ?? []).map(entry => [entry.id, entry]),
        )
        const selectedKeys = new Set(selection.map(positionKey))
        const swapSelectionKey =
          state.skillSelection?.mode === 'swap'
            ? positionKey(state.skillSelection.position)
            : null
        const copySelectionKey =
          state.skillSelection?.mode === 'copy'
            ? positionKey(state.skillSelection.position)
            : null
        const playableKeys = new Set<string>()
        const requiredBoardLetter = getBoggleResolvedRequiredLetter(
          state.resolvedChallenge,
        )
        const gravityStartsAtMs = state.lastResolution?.gravityStartsAtMs
        const elapsedSinceResolution =
          state.lastResolution === null
            ? 0
            : Math.max(0, state.timeMs - state.lastResolution.startedAtMs)

        ensureTileVisuals(state.rows, state.cols)
        ensureClearedLetterVisuals(state.lastResolution?.cleared.length ?? 0)
        syncCanvasMetrics(state, { layout })

        boardGraphics.clear()
        pathGraphics.clear()

        if (isIntervalPrepBlockingPlay(state)) {
          for (const visual of tileVisuals) {
            hideTileVisual(visual)
          }
          for (const clearedLetter of clearedLetterVisuals) {
            clearedLetter.visible = false
          }
          const prepEndsAt = state.intervalPlayBeginsAtMs!
          const prepRemainingMs = prepEndsAt - state.timeMs
          const count = Math.max(
            1,
            Math.min(
              BOGGLE_INTERVAL_PREP_COUNT,
              Math.ceil(prepRemainingMs / BOGGLE_INTERVAL_PREP_STEP_MS),
            ),
          )
          prepCountdownText.text = String(count)
          const prepFontSize = Math.max(64, Math.min(width, height) * 0.26)
          if (prepCountdownStyle.fontSize !== prepFontSize) {
            prepCountdownStyle.fontSize = prepFontSize
          }
          prepCountdownText.position.set(width / 2, height / 2)
          prepCountdownText.visible = true
          setBoggleBoardSurfaceSnapshot(null)
          return
        }

        prepCountdownText.visible = false

        if (state.activeSkill === null) {
          if (selection.length === 0) {
            for (let row = 0; row < state.rows; row += 1) {
              for (let col = 0; col < state.cols; col += 1) {
                if (state.board[row]?.[col]) {
                  playableKeys.add(`${row}:${col}`)
                }
              }
            }
          } else {
            for (let row = 0; row < state.rows; row += 1) {
              for (let col = 0; col < state.cols; col += 1) {
                const cell = state.board[row]?.[col]
                if (!cell) {
                  continue
                }

                if (isPlayableSelectionPosition(state, { col, row })) {
                  playableKeys.add(`${row}:${col}`)
                }
              }
            }
          }
        }

        if (selection.length > 1) {
          for (let index = 1; index < selection.length; index += 1) {
            const prev = selection[index - 1]!
            const curr = selection[index]!
            const cPrev = positionCenter(layout, prev)
            const cCurr = positionCenter(layout, curr)
            const dx = cCurr.x - cPrev.x
            const dy = cCurr.y - cPrev.y
            const chord = Math.hypot(dx, dy)
            if (chord < 1e-6) {
              const colorFrom = selectionPathLetterColor(index - 1)
              const colorTo = selectionPathLetterColor(index)
              drawDoubledTileRepeatArcStroke(
                pathGraphics,
                cCurr.x,
                cCurr.y,
                layout.tileSize,
                colorFrom,
                colorTo,
              )
              continue
            }

            const ux = dx / chord
            const uy = dy / chord

            const preferredEndpointInsetPx = Math.max(
              layout.tileSize * PATH_CHEVRON_ANCHOR_INSET_FROM_TILE_CENTER_RATIO,
              10,
            )
            const symmetricHalfBudgetPx = Math.max(
              0,
              (chord - PATH_SEGMENT_MIDSTUB_MIN_PX) / 2,
            )
            const endpointInsetPx = Math.min(preferredEndpointInsetPx, symmetricHalfBudgetPx)
            const { halfChordLengthPx } = selectionChevronShapePx(layout.tileSize)

            const minStubRemainderPx = chord - PATH_SEGMENT_MIDSTUB_MIN_PX
            let startInsetPx = Math.max(0, endpointInsetPx - halfChordLengthPx)
            startInsetPx = Math.min(startInsetPx, minStubRemainderPx - endpointInsetPx) + 8

            const segStartX = cPrev.x + ux * startInsetPx
            const segStartY = cPrev.y + uy * startInsetPx
            const segEndX = cCurr.x - ux * endpointInsetPx
            const segEndY = cCurr.y - uy * endpointInsetPx

            const nx = uy
            const ny = -ux
            const stubLen = Math.hypot(segEndX - segStartX, segEndY - segStartY)
            const bowScale =
              stubLen > 1e-3
                ? Math.min(1, stubLen / (layout.tileSize * PATH_BOW_SCALE_STUB_DIVISOR_RATIO))
                : 0
            const epOff = PATH_BOW_ENDPOINT_OFFSET_PX * bowScale
            const bowDepth = PATH_BOW_CONTROL_DEPTH_PX * bowScale
            const sx = segStartX + nx * epOff
            const sy = segStartY + ny * epOff
            const ex = segEndX + nx * epOff
            const ey = segEndY + ny * epOff
            const cx = (sx + ex) / 2 + nx * bowDepth
            const cy = (sy + ey) / 2 + ny * bowDepth

            const colorFrom = selectionPathLetterColor(index - 1)
            const colorTo = selectionPathLetterColor(index)
            const steps = Math.max(
              PATH_RAINBOW_STEP_MIN,
              Math.ceil(stubLen / PATH_RAINBOW_STEP_PX),
            )

            for (let step = 0; step < steps; step += 1) {
              const t0 = step / steps
              const t1 = (step + 1) / steps
              const p0 = quadraticBezierXY(sx, sy, cx, cy, ex, ey, t0)
              const p1 = quadraticBezierXY(sx, sy, cx, cy, ex, ey, t1)
              const tm = (t0 + t1) / 2
              const blendT =
                index === 1 ? easeInPow(tm, PATH_SELECTION_DESAT_STROKE_BLEND_POWER) : tm
              pathGraphics
                .moveTo(p0.x, p0.y)
                .lineTo(p1.x, p1.y)
                .stroke({
                  alpha: PATH_ALPHA,
                  color: mixRgb(colorFrom, colorTo, blendT),
                  width: PATH_WIDTH,
                })
            }

            drawSelectionPathChevronTowardInbound(
              pathGraphics,
              ex,
              ey,
              ux,
              uy,
              layout.tileSize,
              mixRgb(colorTo, 0x000000, 0.22),
            )
          }
        }

        if (selection.length === 1) {
          const end = selection[selection.length - 1]!
          const endCenter = positionCenter(layout, end)
          pathGraphics.circle(endCenter.x, endCenter.y, SELECTION_END_MARKER_RADIUS).fill({
            alpha: SELECTION_END_MARKER_ALPHA,
            color: selectionPathLetterColor(0),
          })
        }

        for (let row = 0; row < state.rows; row += 1) {
          for (let col = 0; col < state.cols; col += 1) {
            const visual = tileVisuals[row * state.cols + col]
            const position = { col, row }
            const key = positionKey(position)
            const cell = state.board[row]?.[col]
            if (!cell) {
              if (visual) {
                hideTileVisual(visual)
              }

              const center = positionCenter(layout, position)
              boardGraphics
                .circle(center.x, center.y, layout.tileSize * EMPTY_SLOT_RADIUS_RATIO)
                .fill({
                  alpha: EMPTY_SLOT_ALPHA,
                  color: EMPTY_SLOT_FILL,
                })
              continue
            }

            const isSelected = selectedKeys.has(key)
            const isSwapSelected = swapSelectionKey === key
            const isCopySelected = copySelectionKey === key
            const isPressed = pressedKey === key
            const selectionFillPulse =
              isSelected && !isPressed ? selectionFillPulseStrength(key, wallClockMs) : 0
            const isPlayable = playableKeys.has(key)
            const isPlayedAndPlayable = isSelected && isPlayable
            const isPlayedAndBlocked = isSelected && !isPlayable
            const isDimmedUnplayable =
              state.activeSkill === null &&
              state.selection.length > 0 &&
              !isPlayable &&
              !isSelected &&
              !isPressed &&
              !isSwapSelected &&
              !isCopySelected
            const goldRushActive = isBoggleGoldRushActive(state)
            const displayKind = resolveLiveCellDisplayKind(cell, goldRushActive)
            const isGold = displayKind === BOGGLE_GOLD_TILE_KIND
            const isAnchorLetterTile =
              requiredBoardLetter !== null &&
              cell.letter.toUpperCase() === requiredBoardLetter.toUpperCase()
            const tileShape = getTileShapeKind(cell, goldRushActive)
            const moved = movedLookup.get(cell.id)
            const spawned = spawnedLookup.get(cell.id)

            /** Only spawned tiles stagger in; opaque during delay looks like a phantom above the hole. */
            let spawnRevealAlpha = 1
            const visualPosition = moved
              ? gravityStartsAtMs !== undefined &&
                moved.viaCol !== undefined &&
                moved.viaRow !== undefined
                ? (() => {
                    if (elapsedSinceResolution <= gravityStartsAtMs) {
                      const rotateProgress = easeInOutSine(
                        clamp(elapsedSinceResolution / gravityStartsAtMs, 0, 1),
                      )
                      return {
                        col:
                          moved.fromCol + (moved.viaCol - moved.fromCol) * rotateProgress,
                        row:
                          moved.fromRow + (moved.viaRow - moved.fromRow) * rotateProgress,
                      }
                    }

                    const gravityDurationMs = Math.max(
                      1,
                      state.lastResolution!.durationMs - gravityStartsAtMs,
                    )
                    const gravityProgress = easeInCubic(
                      clamp(
                        (elapsedSinceResolution - gravityStartsAtMs) / gravityDurationMs,
                        0,
                        1,
                      ),
                    )
                    return {
                      col: moved.viaCol + (moved.toCol - moved.viaCol) * gravityProgress,
                      row: moved.viaRow + (moved.toRow - moved.viaRow) * gravityProgress,
                    }
                  })()
                : {
                    col: moved.fromCol + (moved.toCol - moved.fromCol) * easedProgress,
                    row: moved.fromRow + (moved.toRow - moved.fromRow) * easedProgress,
                  }
              : spawned
                ? (() => {
                    const spawnDelayMs = spawned.spawnDelayMs ?? 0
                    const elapsedAfterDelay = elapsedSinceResolution - spawnDelayMs
                    const spawnLocalT = clamp(
                      elapsedAfterDelay / BOGGLE_RESOLVE_ANIMATION_MS,
                      0,
                      1,
                    )
                    if (elapsedAfterDelay <= 0) {
                      spawnRevealAlpha = 0
                    } else {
                      spawnRevealAlpha = easeOutCubic(spawnLocalT)
                    }
                    const spawnEased = easeOutCubic(spawnLocalT)
                    const rowStart = spawned.toRow - BOGGLE_SPAWN_START_ROWS_ABOVE
                    return {
                      col,
                      row: rowStart + (spawned.toRow - rowStart) * spawnEased,
                    }
                  })()
                : { col, row }
            const center = {
              x:
                layout.originX +
                visualPosition.col * layout.cellSize +
                layout.cellSize / 2,
              y:
                layout.originY +
                visualPosition.row * layout.cellSize +
                layout.cellSize / 2,
            }
            const tileScale = isPressed ? TILE_PRESSED_SCALE : 1
            if (!visual) {
              continue
            }

            if (spawnRevealAlpha <= 0) {
              hideTileVisual(visual)
              continue
            }

            visual.shadow.clear()
            drawTileShapePath(
              visual.shadow,
              tileShape,
              center.x,
              center.y + layout.tileSize * 0.03,
              layout.tileSize * 0.43 * tileScale,
            )
            visual.shadow.fill({
              alpha: TILE_SHADOW_ALPHA * spawnRevealAlpha,
              color: TILE_SHADOW_COLOR,
            })
            visual.shadow.visible = true

            visual.tileOutline.clear()
            const showTileOutline = isPlayable || isAnchorLetterTile
            if (showTileOutline) {
              drawTileShapePath(
                visual.tileOutline,
                tileShape,
                center.x,
                center.y,
                (layout.tileSize / 2) * tileScale +
                  (isPlayedAndPlayable ? PLAYED_OUTLINE_OFFSET : 0),
              )
              visual.tileOutline.stroke({
                alpha:
                  spawnRevealAlpha *
                  (isAnchorLetterTile
                    ? ANCHOR_LETTER_OUTLINE_ALPHA
                    : isPlayedAndPlayable
                      ? PLAYED_OUTLINE_ALPHA
                      : PLAYABLE_OUTLINE_ALPHA),
                color: isAnchorLetterTile ? ANCHOR_LETTER_OUTLINE : PLAYABLE_OUTLINE,
                width: PLAYABLE_OUTLINE_WIDTH,
              })
            }
            visual.tileOutline.visible = showTileOutline

            visual.selectedInnerOutline.clear()
            if (isPlayedAndPlayable) {
              drawTileShapePath(
                visual.selectedInnerOutline,
                tileShape,
                center.x,
                center.y,
                (layout.tileSize / 2) * tileScale - PLAYED_SEPARATOR_WIDTH / 2,
              )
              visual.selectedInnerOutline.stroke({
                alpha: PLAYED_SEPARATOR_ALPHA * spawnRevealAlpha,
                color: PLAYED_SEPARATOR_COLOR,
                width: PLAYED_SEPARATOR_WIDTH,
              })
            }
            visual.selectedInnerOutline.visible = isPlayedAndPlayable

            visual.goldOutline.clear()
            if (isGold) {
              drawTileShapePath(
                visual.goldOutline,
                tileShape,
                center.x,
                center.y,
                (layout.tileSize / 2) * tileScale + 3,
              )
            }
            visual.goldOutline.visible = isGold

            visual.tile.clear()
            drawTileShapePath(
              visual.tile,
              tileShape,
              center.x,
              center.y,
              (layout.tileSize / 2) * tileScale,
            )
            const idleFillColor = getIdleTileFillColor(displayKind)
            let baseFillAlpha = TILE_IDLE_ALPHA
            let baseFillColor = idleFillColor
            if (isPlayedAndBlocked) {
              baseFillAlpha = PLAYED_INACTIVE_LETTER_ALPHA
            } else if (isDimmedUnplayable) {
              baseFillAlpha = TILE_IDLE_ALPHA - UNPLAYABLE_TILE_FILL_ALPHA_OFFSET
            }

            let tileFillColor: number
            let tileFillAlpha: number
            if (isPressed) {
              tileFillColor = TILE_PRESSED
              tileFillAlpha = TILE_EMPHASIS_ALPHA
            } else if (isSwapSelected) {
              tileFillColor = SWAP_FILL
              tileFillAlpha = TILE_EMPHASIS_ALPHA
            } else if (isCopySelected) {
              tileFillColor = COPY_FILL
              tileFillAlpha = TILE_EMPHASIS_ALPHA
            } else if (selectionFillPulse > 0) {
              tileFillColor = mixRgb(baseFillColor, TILE_ACTIVE, selectionFillPulse)
              tileFillAlpha =
                baseFillAlpha + (TILE_EMPHASIS_ALPHA - baseFillAlpha) * selectionFillPulse
            } else {
              tileFillColor = baseFillColor
              tileFillAlpha = baseFillAlpha
            }

            visual.tile.fill({
              alpha: tileFillAlpha * spawnRevealAlpha,
              color: tileFillColor,
            })
            visual.tile.visible = true

            const isVowel = BOGGLE_VOWELS.has(cell.letter)
            const isRareLetter = (RARE_LETTERS as readonly string[]).includes(cell.letter)
            const isUncommonLetter =
              !isRareLetter &&
              BOGGLE_UNCOMMON_LETTERS.includes(
                cell.letter as (typeof BOGGLE_UNCOMMON_LETTERS)[number],
              )
            visual.letter.text = cell.letter
            visual.letter.style = isVowel ? vowelLetterStyle : letterStyle
            visual.letter.resolution = renderResolution
            visual.letter.position.set(
              snapToPixel(center.x, renderResolution),
              snapToPixel(center.y, renderResolution),
            )
            visual.letter.alpha =
              spawnRevealAlpha *
              (isDimmedUnplayable ? UNPLAYABLE_LETTER_ALPHA : LETTER_FULL_ALPHA)
            visual.letter.scale.set(tileScale)
            const idleLetterTint = getIdleTileTextColor(displayKind)
            if (isPressed) {
              visual.letter.tint = TILE_ACTIVE_TEXT
            } else if (selectionFillPulse > 0) {
              visual.letter.tint = mixRgb(idleLetterTint, TILE_ACTIVE_TEXT, selectionFillPulse)
            } else if (isSwapSelected) {
              visual.letter.tint = SWAP_TEXT
            } else if (isCopySelected) {
              visual.letter.tint = COPY_TEXT
            } else {
              visual.letter.tint = getIdleTileTextColor(displayKind)
            }
            visual.letter.visible = true

            if (isVowel) {
              const radius = vowelLetterCircleRadiusPx(layout, tileScale)
              const vowelFillAlpha = 0.15
              const vowelStrokeAlpha = 0.13
              visual.letterCircle.clear()
              visual.letterCircle.circle(center.x, center.y, radius)
              visual.letterCircle.fill({
                alpha:
                  spawnRevealAlpha *
                  (isDimmedUnplayable
                    ? vowelFillAlpha * UNPLAYABLE_LETTER_ALPHA
                    : vowelFillAlpha),
                color: visual.letter.tint,
              })
              visual.letterCircle.stroke({
                alpha:
                  spawnRevealAlpha *
                  (isDimmedUnplayable
                    ? vowelStrokeAlpha * UNPLAYABLE_LETTER_ALPHA
                    : vowelStrokeAlpha),
                color: visual.letter.tint,
                width: 2 * tileScale,
              })
              visual.letterCircle.visible = true
            } else {
              visual.letterCircle.visible = false
            }

            if (isRareLetter) {
              const sizeScale = isVowel ? 0.45 : 0.53
              const half = ((layout.tileSize * sizeScale) / 2) * tileScale
              const rareFillAlpha = isVowel ? 0.15 : 0.2
              const rareStrokeAlpha = 0.13
              visual.letterDiamond.clear()
              visual.letterDiamond.rotation = Math.PI / 4
              visual.letterDiamond.position.set(center.x, center.y)
              visual.letterDiamond.rect(-half, -half, half * 2, half * 2)
              visual.letterDiamond.fill({
                alpha:
                  spawnRevealAlpha *
                  (isDimmedUnplayable
                    ? rareFillAlpha * UNPLAYABLE_LETTER_ALPHA
                    : rareFillAlpha),
                color: isVowel
                  ? VOWEL_AND_UNCOMMON_LETTER_ACCENT
                  : UNCOMMON_LETTER_ACCENT,
              })
              visual.letterDiamond.stroke({
                alpha:
                  spawnRevealAlpha *
                  (isDimmedUnplayable
                    ? rareStrokeAlpha * UNPLAYABLE_LETTER_ALPHA
                    : rareStrokeAlpha),
                color: visual.letter.tint,
                width: 2 * tileScale,
              })
              visual.letterDiamond.visible = true
            } else {
              visual.letterDiamond.rotation = 0
              visual.letterDiamond.position.set(0, 0)
              visual.letterDiamond.visible = false
            }

            if (isUncommonLetter) {
              const sizeScale = isVowel ? 0.45 : 0.53
              const half = ((layout.tileSize * sizeScale) / 2) * tileScale
              const uncommonFillAlpha = isVowel ? 0.15 : 0.2
              const uncommonStrokeAlpha = 0.13
              visual.letterSquare.clear()
              visual.letterSquare.rect(
                center.x - half,
                center.y - half,
                half * 2,
                half * 2,
              )
              visual.letterSquare.fill({
                alpha:
                  spawnRevealAlpha *
                  (isDimmedUnplayable
                    ? uncommonFillAlpha * UNPLAYABLE_LETTER_ALPHA
                    : uncommonFillAlpha),
                color: isVowel
                  ? VOWEL_AND_UNCOMMON_LETTER_ACCENT
                  : UNCOMMON_LETTER_ACCENT,
              })
              visual.letterSquare.stroke({
                alpha:
                  spawnRevealAlpha *
                  (isDimmedUnplayable
                    ? uncommonStrokeAlpha * UNPLAYABLE_LETTER_ALPHA
                    : uncommonStrokeAlpha),
                color: visual.letter.tint,
                width: 2 * tileScale,
              })
              visual.letterSquare.visible = true
            } else {
              visual.letterSquare.visible = false
            }

            visual.bonusBadge.visible = false
          }
        }

        for (const letter of clearedLetterVisuals) {
          letter.visible = false
        }

        for (const [index, cleared] of (state.lastResolution?.cleared ?? []).entries()) {
          const letter = clearedLetterVisuals[index]
          if (!letter) {
            continue
          }

          const alpha = 1 - easedProgress
          const scale = 1 - easedProgress * 0.22
          const center = positionCenter(layout, { col: cleared.col, row: cleared.row })
          letter.text = cleared.letter
          letter.style = BOGGLE_VOWELS.has(cleared.letter)
            ? vowelLetterStyle
            : letterStyle
          letter.resolution = renderResolution
          letter.position.set(
            snapToPixel(center.x, renderResolution),
            snapToPixel(center.y - easedProgress * 16, renderResolution),
          )
          letter.alpha = alpha
          letter.scale.set(scale)
          letter.tint = TILE_TEXT
          letter.visible = true
        }

        if (selectionFillPulseNeedsTicker(state.selection, performance.now())) {
          if (!selectionFillPulseTickerFn) {
            const pulseTickerFrame = () => {
              const pulseState = store.getState()
              const now = performance.now()
              if (!selectionFillPulseNeedsTicker(pulseState.selection, now)) {
                app.ticker.remove(pulseTickerFrame)
                selectionFillPulseTickerFn = null
                return
              }

              draw()
            }

            selectionFillPulseTickerFn = pulseTickerFrame
            app.ticker.add(pulseTickerFrame)
          }
        }
      }

      const resize = () => {
        const currentHost = hostRef.current
        if (!currentHost) {
          return
        }

        const canvasWidth = Math.max(1, currentHost.clientWidth)
        const canvasHeight = Math.max(1, currentHost.clientHeight)
        app.renderer.resize(canvasWidth, canvasHeight)
        app.canvas.style.width = `${canvasWidth}px`
        app.canvas.style.height = `${canvasHeight}px`
        syncCanvasMetrics(store.getState(), { measureRect: true })
        draw()
      }

      const getCanvasPosition = (
        clientX: number,
        clientY: number,
        state = store.getState(),
        measureRect = false,
        targetMode: TileHitTargetMode,
      ) => {
        if (
          canvasMetricsRef.current === null ||
          measureRect ||
          canvasMetricsRef.current.rows !== state.rows ||
          canvasMetricsRef.current.cols !== state.cols ||
          canvasMetricsRef.current.rendererWidth !== app.renderer.width ||
          canvasMetricsRef.current.rendererHeight !== app.renderer.height
        ) {
          syncCanvasMetrics(state, { measureRect })
        }

        const metrics = canvasMetricsRef.current
        if (!metrics || metrics.rect.width <= 0 || metrics.rect.height <= 0) {
          return null
        }

        return getPointerCell(
          ((clientX - metrics.rect.left) / metrics.rect.width) * metrics.rendererWidth,
          ((clientY - metrics.rect.top) / metrics.rect.height) * metrics.rendererHeight,
          metrics.layout,
          metrics.rows,
          metrics.cols,
          position =>
            getTileShapeKind(
              state.board[position.row]?.[position.col],
              isBoggleGoldRushActive(state),
            ),
          targetMode,
        )
      }

      const releaseActivePointerCapture = (pointerId: number) => {
        if (app.canvas.hasPointerCapture(pointerId)) {
          app.canvas.releasePointerCapture(pointerId)
        }
      }

      const resetPrimaryPointerTracking = () => {
        pointerDownRef.current = false
        pointerDragActiveRef.current = false
        pointerLostTileSinceLastTrigger = false
        pointerLastTriggeredCellRef.current = null
        pointerPressedCellRef.current = null
        pointerStartCellRef.current = null
        pointerStartCoordsRef.current = null
        if (activePointerIdRef.current !== null) {
          releaseActivePointerCapture(activePointerIdRef.current)
          activePointerIdRef.current = null
        }
      }

      const handlePointerDown = (event: PointerEvent) => {
        const state = store.getState()
        if (isIntervalPrepBlockingPlay(state)) {
          event.preventDefault()
          return
        }
        const position = getCanvasPosition(
          event.clientX,
          event.clientY,
          state,
          true,
          'idle',
        )

        if (activePointerIdRef.current !== null) {
          if (event.button !== 0 || !position) {
            return
          }

          event.preventDefault()
          if (state.activeSkill !== null) {
            store.useActiveSkillOnTile(position)
          } else {
            store.tapSelection(position)
          }
          return
        }

        if (!position) {
          return
        }

        const { activeSkill } = state
        if (activeSkill !== null) {
          event.preventDefault()
          store.useActiveSkillOnTile(position)
          return
        }

        if (event.button !== 0) {
          return
        }

        activePointerIdRef.current = event.pointerId
        try {
          app.canvas.setPointerCapture(event.pointerId)
        } catch {
          // setPointerCapture can fail for inactive or unsupported pointers.
        }

        pointerDownRef.current = true
        pointerDragActiveRef.current = false
        pointerLostTileSinceLastTrigger = false
        pointerLastTriggeredCellRef.current = position
        pointerPressedCellRef.current = position
        pointerStartCellRef.current = position
        pointerStartCoordsRef.current = { x: event.clientX, y: event.clientY }
        store.tapSelection(position)
      }

      const handlePointerMove = (event: PointerEvent) => {
        if (
          !pointerDownRef.current ||
          activePointerIdRef.current !== event.pointerId
        ) {
          return
        }

        const state = store.getState()
        const startCell = pointerStartCellRef.current
        const startCoords = pointerStartCoordsRef.current
        if (!startCell || !startCoords) {
          return
        }

        const deltaX = event.clientX - startCoords.x
        const deltaY = event.clientY - startCoords.y
        const movedEnough =
          deltaX * deltaX + deltaY * deltaY >=
          TAP_DRAG_THRESHOLD_PX * TAP_DRAG_THRESHOLD_PX
        const position = getCanvasPosition(
          event.clientX,
          event.clientY,
          state,
          false,
          'drag',
        )

        if (!movedEnough && (!position || positionsMatch(position, startCell))) {
          return
        }

        if (!pointerDragActiveRef.current) {
          pointerDragActiveRef.current = true
        }

        const selEnd = state.selection[state.selection.length - 1]
        if (position === null) {
          pointerLostTileSinceLastTrigger = true
        } else if (
          pointerDragActiveRef.current &&
          state.repeatTileMinGap > 0 &&
          pointerLostTileSinceLastTrigger &&
          selEnd &&
          pointerLastTriggeredCellRef.current !== null &&
          positionsMatch(position, selEnd) &&
          positionsMatch(position, pointerLastTriggeredCellRef.current)
        ) {
          pointerLostTileSinceLastTrigger = false
          pointerLastTriggeredCellRef.current = position
          store.updateSelection(position)
          draw()
          return
        }

        if (!nullablePositionsMatch(pointerPressedCellRef.current, position)) {
          pointerPressedCellRef.current = position
        }

        const { nextTriggeredPosition, shouldTrigger } = getNextDragTrigger(
          pointerLastTriggeredCellRef.current,
          position,
        )
        pointerLastTriggeredCellRef.current = nextTriggeredPosition

        if (!shouldTrigger || !position) {
          if (position === null) {
            draw()
          }
          return
        }

        store.updateSelection(position)
      }

      const handlePointerUp = (event: PointerEvent) => {
        if (
          !pointerDownRef.current ||
          activePointerIdRef.current !== event.pointerId
        ) {
          return
        }

        const startCell = pointerStartCellRef.current
        const startCoords = pointerStartCoordsRef.current
        const wasDrag = pointerDragActiveRef.current
        const hadPressedCell = pointerPressedCellRef.current !== null
        resetPrimaryPointerTracking()

        if (wasDrag) {
          if (hadPressedCell) {
            draw()
          }
          return
        }

        if (!startCell || !startCoords) {
          return
        }

        const deltaX = event.clientX - startCoords.x
        const deltaY = event.clientY - startCoords.y
        const movedTooFar =
          deltaX * deltaX + deltaY * deltaY >=
          TAP_DRAG_THRESHOLD_PX * TAP_DRAG_THRESHOLD_PX
        if (movedTooFar) {
          if (hadPressedCell) {
            draw()
          }
          return
        }

        const position = getCanvasPosition(
          event.clientX,
          event.clientY,
          store.getState(),
          false,
          'idle',
        )
        if (!position) {
          if (hadPressedCell) {
            draw()
          }
          return
        }

        if (hadPressedCell) {
          draw()
        }
      }

      const handleContextMenu = (event: MouseEvent) => {
        event.preventDefault()
      }

      const observer = new ResizeObserver(() => {
        resize()
      })

      observer.observe(hostRef.current)
      const handlePointerCancel = (event: PointerEvent) => {
        if (activePointerIdRef.current !== event.pointerId) {
          return
        }

        const wasDrag = pointerDragActiveRef.current
        const hadPressedCell = pointerPressedCellRef.current !== null
        resetPrimaryPointerTracking()
        if (wasDrag && hadPressedCell) {
          draw()
        }
      }

      app.canvas.addEventListener('pointerdown', handlePointerDown)
      app.canvas.addEventListener('pointermove', handlePointerMove)
      app.canvas.addEventListener('pointercancel', handlePointerCancel)
      app.canvas.addEventListener('contextmenu', handleContextMenu)
      window.addEventListener('pointerup', handlePointerUp)

      const drawIfAnimating = () => {
        if (store.hasTransientState()) {
          draw()
        }
      }

      const unsubscribeUi = store.subscribeUi(draw)
      const unsubscribeAll = store.subscribe(drawIfAnimating)
      const unsubscribeTimeForPrep = store.subscribeTime(() => {
        if (isIntervalPrepBlockingPlay(store.getState())) {
          draw()
        }
      })
      appRef.current = app
      resize()
      requestAnimationFrame(() => {
        if (!mounted) {
          return
        }
        store.setBoggleBoardSurfaceReady(true)
      })

      cleanup = () => {
        resetBoggleBoardSurfaceBridge()
        store.setBoggleBoardSurfaceReady(false)
        if (typeof document !== 'undefined' && document.fonts?.removeEventListener) {
          document.fonts.removeEventListener('loadingdone', refreshPixiTextStyles)
        }
        observer.disconnect()
        unsubscribeUi()
        unsubscribeAll()
        unsubscribeTimeForPrep()
        if (selectionFillPulseTickerFn) {
          app.ticker.remove(selectionFillPulseTickerFn)
          selectionFillPulseTickerFn = null
        }
        destroyTileVisuals()
        for (const child of clearedTextLayer.removeChildren()) {
          child.destroy()
        }
        clearedLetterVisuals = []
        resetPrimaryPointerTracking()
        app.canvas.removeEventListener('pointerdown', handlePointerDown)
        app.canvas.removeEventListener('pointermove', handlePointerMove)
        app.canvas.removeEventListener('pointercancel', handlePointerCancel)
        app.canvas.removeEventListener('contextmenu', handleContextMenu)
        window.removeEventListener('pointerup', handlePointerUp)
      }
    }

    void setup()

    return () => {
      mounted = false
      cleanup?.()
      canvasMetricsRef.current = null
      appRef.current?.destroy(true, { children: true })
      appRef.current = null
    }
  }, [hostRef, store])
}
